import { Validation } from 'vuelidate';
import { Role, RoleValues } from '@/utils/dictionaries';
import { StatementType, LeaveStep } from '@/types/services';
import { TOKENS } from '@/utils/mask';

export const mapStatementName = {
  [StatementType.NOTICE_RECOVERY]:
    'Заявление о совершении исполнительной надписи',
  [StatementType.RUZDI]:
    'Заявление о выдаче выписки из реестра уведомлений о залоге движимого имущества удаленно',
  [StatementType.SECURING_EVIDENCE]:
    'Заявление об  обеспечении доказательств в сети “Интернет”',
  [StatementType.ACCEPTANCE_DEPOSIT]:
    'Заявление о совершении удаленно нотариального действия по принятию в депозит нотариуса безналичных денежных средств с целью исполнения обязательства',
  [StatementType.TRANSFER_EL_DOCS]:
    'Заявление о передаче электронных документов физических и юридических лиц другим физическим и юридическим лицам, совершаемой удаленно',
  [StatementType.CORRECT_TRANSLATION]:
    'Заявление о свидетельствовании верности перевода нотариусом, совершенного удаленно',
  [StatementType.ACCEPTANCE_ESCROW]:
    'Заявление о принятии на депонирование безналичных денежных средств удаленно для расчета по сделкам',
  [StatementType.STORAGE_ACCEPT]:
    'Заявление о принятии на хранение электронного документа (пакета электронных документов) удаленно',
  [StatementType.STORAGE_ISSUE]:
    'Заявление о выдаче принятого на хранение электронного документа (пакета электронных документов)',
  [StatementType.DOCUMENT_CONVERSION]:
    'Заявление об удостоверении равнозначности электронного документа, изготовленного нотариусом в ином формате, электронному документу, представленному нотариусу удаленно',
  [StatementType.ROD]:
    'Заявление о внесении сведений в реестр распоряжений об отмене доверенностей',
  [StatementType.EXTRACTION_ROD]:
    'Заявление о совершении удаленно нотариального действия по выдаче выписки из реестра распоряжений об отмене доверенностей, за исключением нотариально удостоверенных доверенностей',
  [StatementType.EGRN_CONFIRM]:
    'Заявление об удостоверении нотариусом удаленно факта наличия сведений в Едином государственном реестре недвижимости',
};

export const dateFormat = (date: string) => {
  const [day, month, year] = date.split('.');
  return `${year}-${month}-${day}`;
};

export const checkInvalid = (field: Validation, invalidFields: boolean[]) => {
  if (field.$invalid) {
    invalidFields.push(true);
  }
};

export const MIN_DATE = '1900-01-01';

export const DealForm = {
  SIMPLE: 'SIMPLE',
  NOTARIAL: 'NOTARIAL',
};

export const dealFormToLabel = {
  [DealForm.SIMPLE]:
    'Простая письменная форма сделки (заявление подается совместно кредитором и должником)',
  [DealForm.NOTARIAL]:
    'Нотариальная форма сделки (заявление может быть подано должником)',
};

export const Text = {
  refusalDetailsTitle:
    'Реквизиты счета для возврата заявителю суммы платежа за совершение нотариального действия в случае отказа в совершении нотариального действия',
  hasPrivilegeNote:
    '*При наличии основания для освобождения от уплаты нотариального тарифа (в соответствии со ст. 333.35 и ст. 333.38 Налогового кодекса РФ) данная информация указывается в поле "Примечание"',
  accountNumberHelperP1:
    '*Eсли заявитель является юридическим лицом, то указываются реквизиты расчетного счета.',
  accountNumberHelperP2:
    'Если заявитель юридическое лицо (кредитная организация), не имеющее расчетного счета, данное поле не заполняется.',
  accountBankDetailsNumberHelperP1:
      '*Необходимо указать счёт заявителя для возврата. Если заявитель является юридическим лицом, счёт физического лица указать невозможно.',
};

/**
 * step - текущий щаг
 * stepThis должен содержать:
 * touchFields(function) - Метод для валидации полей шага;
 * invalidFields(boolean[]);
 * assemblyModel(function) - Метод возвращающий модель данных шага,
 * для сохранения черновика;
 * */
export const leaveStep: LeaveStep = async ({ stepThis, direction, step }) => {
  await stepThis.touchFields();

  if (stepThis.invalidFields.length) {
    await stepThis.$vuetify.goTo('#scroll-target', { duration: 100 });
  } else {
    stepThis.$emit(direction, {
      fragment: stepThis.assemblyModel(),
      step,
    });
  }
};

const getPersonDataUserCert = (subject: any) => {
  const { person, trustee, assignee } = subject;
  const dataUserCert = {
    subjectType: Role.PERSON,
  };

  if (trustee) {
    return {
      ...dataUserCert,
      userLastName: trustee.lastName,
      userFirstName: trustee.firstName,
      userMiddleName: trustee.patronymic,
      snils: trustee.snils,
      inn: trustee.inn,
    };
  }

  if (assignee) {
    return {
      ...dataUserCert,
      userLastName: assignee.lastName,
      userFirstName: assignee.firstName || assignee.name,
      userMiddleName: assignee.patronymic,
      snils: assignee.snils,
      inn: assignee.inn,
    };
  }

  return {
    ...dataUserCert,
    userLastName: person.lastName,
    userFirstName: person.firstName,
    userMiddleName: person.patronymic,
    snils: person.snils,
    inn: person.inn,
    ogrn: person.isBusinessman && person.ogrn,
    isBusinessman: person.isBusinessman,
  };
};

const getOrganizationDataUserCert = (subject: any) => {
  const { organization, trustee, assignee } = subject;
  const dataUserCert = {
    subjectType: Role.ORGANIZATION,
    inn: organization.inn,
    ogrn: organization.ogrn,
  };

  if (trustee) {
    return {
      ...dataUserCert,
      userLastName: trustee.lastName,
      userFirstName: trustee.firstName,
      userMiddleName: trustee.patronymic,
      snils: trustee.snils,
    };
  }

  if (assignee) {
    return {
      ...dataUserCert,
      userLastName: assignee.lastName,
      userFirstName: assignee.firstName || assignee.name,
      userMiddleName: assignee.patronymic,
      snils: assignee.snils,
    };
  }

  return {
    ...dataUserCert,
    userLastName: organization.assigneeLastName,
    userFirstName: organization.assigneeName,
    userMiddleName: organization.assigneePatronymic,
    snils: organization.assigneeSnils,
  };
};

const getForeignOrganizationDataUserCert = (subject: any) => {
  const { foreignOrganization, trustee, assignee } = subject;
  const dataUserCert = {
    subjectType: Role.FOREIGN_ORGANIZATION,
    inn: foreignOrganization.inn,
  };

  if (trustee) {
    return {
      ...dataUserCert,
      userLastName: trustee.lastName,
      userFirstName: trustee.firstName,
      userMiddleName: trustee.patronymic,
      snils: trustee.snils,
    };
  }

  if (assignee) {
    return {
      ...dataUserCert,
      userLastName: assignee.lastName,
      userFirstName: assignee.firstName || assignee.name,
      userMiddleName: assignee.patronymic,
      snils: assignee.snils,
    };
  }

  return {
    ...dataUserCert,
    lastName: foreignOrganization.assigneeLastName,
    firstName: foreignOrganization.assigneeName,
    patronymic: foreignOrganization.assigneePatronymic,
    snils: foreignOrganization.assigneeSnils,
  };
};

const parseDataUserCertificateManual = (subject: any) => {
  if (subject.person) return getPersonDataUserCert(subject);

  if (subject.organization) {
    return getOrganizationDataUserCert(subject);
  }

  return getForeignOrganizationDataUserCert(subject);
};

const parseDataUserCertificateMrpa = (subject: any, signerType: RoleValues) => {
  const { trustee, trusteeOrganization, organization, person } = subject;

  const getTrustee = () => ({
    subjectType: Role.PERSON,
    userLastName: trustee.lastName,
    userFirstName: trustee.firstName,
    userMiddleName: trustee.patronymic,
    snils: trustee.snils,
    inn: trustee.inn,
  });

  const getTrusteeOrganizationSignerPerson = () => ({
    subjectType: Role.PERSON,
    userLastName: trusteeOrganization.assignee.lastName,
    userFirstName: trusteeOrganization.assignee.firstName,
    userMiddleName: trusteeOrganization.assignee.patronymic,
    snils: trusteeOrganization.assignee.snils,
    inn: trusteeOrganization.assignee.inn,
  });

  const getTrusteeOrganizationSignerOrganization = () => ({
    subjectType: Role.ORGANIZATION,
    inn: trusteeOrganization.inn,
    ogrn: trusteeOrganization.ogrn,

    userLastName: trusteeOrganization.assignee.lastName,
    userFirstName: trusteeOrganization.assignee.firstName,
    userMiddleName: trusteeOrganization.assignee.patronymic,
    snils: trusteeOrganization.assignee.snils,
  });

  if (person) {
    if (trustee) {
      if (signerType === Role.PERSON) {
        return getTrustee();
      }

      throw new Error('Ошибка: Заявитель ФЛ, Представитель ФЛ, подписант ЮЛ');
    }

    // Представитель ЮЛ
    if (signerType === Role.PERSON) {
      return getTrusteeOrganizationSignerPerson();
    }

    return getTrusteeOrganizationSignerOrganization();
  }

  if (organization) {
    if (trustee) {
      if (signerType === Role.PERSON) {
        return getTrustee();
      }

      return {
        subjectType: Role.ORGANIZATION,
        inn: organization.inn,
        ogrn: organization.ogrn,

        userLastName: trustee.lastName,
        userFirstName: trustee.firstName,
        userMiddleName: trustee.patronymic,
        snils: trustee.snils,
      };
    }

    // Представитель ЮЛ
    if (signerType === Role.PERSON) {
      return getTrusteeOrganizationSignerPerson();
    }

    return getTrusteeOrganizationSignerOrganization();
  }

  throw new Error('Ошибка: в МЧД нет ИЮЛ');
};

/**
 * парсит данные заявителя для проверки валидности сертификата
 * subject(object)
 * subject.person, subject.organization, subject.foreignOrganization
 * subject.trustee - данные представителя
 * */
export const parseDataUserCertificate = (
  subject: any,
  signerType: RoleValues
) => {
  const isMrpaStatement = Boolean(
    subject.trustee?.baseAuthority || subject.trusteeOrganization?.baseAuthority
  );

  if (isMrpaStatement) {
    return parseDataUserCertificateMrpa(subject, signerType);
  }

  return parseDataUserCertificateManual(subject);
};

export const RUS_PASSPORT = '21';
export const getIdentityDocumentToken = (code: string) => {
  switch (code) {
    case '01':
      return {
        series: {
          mask: 'R-ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '999999',
          tokens: TOKENS,
        },
      };

    case '02':
      return {
        series: {
          mask: '99',
          tokens: TOKENS,
        },
        number: {
          mask: '0999999',
          tokens: TOKENS,
        },
      };

    case '04':
      return {
        series: {
          mask: 'ББ',
          tokens: TOKENS,
        },
        number: {
          mask: ' 0999999',
          tokens: TOKENS,
        },
      };

    case '06':
      return {
        series: {
          mask: 'ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '999999',
          tokens: TOKENS,
        },
      };

    case '07':
      return {
        series: {
          mask: 'ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '0999999',
          tokens: TOKENS,
        },
      };

    case '03':
    case '05':
    case '08':
    case '10':
    case '11':
    case '12':
    case '13':
    case '14':
    case '15':
    case '23':
    case '61':
    case '81':
    case '91':
      return {
        series: {
          mask: 'SSSSSSSSSSSSSSSSSSSSSSSSS',
          tokens: TOKENS,
        },
        number: {
          mask: 'SSSSSSSSSSSSSSSSSSSSSSSSS',
          tokens: TOKENS,
        },
      };

    case '09':
    case '22':
      return {
        series: {
          mask: '99',
          tokens: TOKENS,
        },
        number: {
          mask: '9999999',
          tokens: TOKENS,
        },
      };
    case '18':
      return {
        series: {
          mask: 'ББ-999',
          tokens: TOKENS,
        },
        number: {
          mask: '9999999',
          tokens: TOKENS,
        },
      };

    case RUS_PASSPORT:
      return {
        series: {
          mask: '9999',
          tokens: TOKENS,
        },
        number: {
          mask: '999999',
          tokens: TOKENS,
        },
      };

    case '24':
      return {
        series: {
          mask: 'ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '9999999',
          tokens: TOKENS,
        },
      };

    case '26':
    case '27':
      return {
        series: {
          mask: 'ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '0999999',
          tokens: TOKENS,
        },
      };

    default:
      return {
        series: {
          mask: 'R-ББ',
          tokens: TOKENS,
        },
        number: {
          mask: '999999',
          tokens: TOKENS,
        },
      };
  }
};
